import {
  WINDOW_ORDER_STATUS,
  WINDOW_ORDER_STATUS_OPTIONS,
  WindowOrderSummary,
} from '@cutr/constants/windows/windows';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ApiError } from '@/api/backend';
import { PaginatedData } from '@/api/backend/agent';
import { BasePagination } from '@/blocks/BasePagination';
import { ConfirmationDialog } from '@/blocks/ConfirmationDialog';
import { Multiselect } from '@/blocks/Multiselect';
import { Button } from '@/primitives/Button';
import { Download, Edit, Icon, Trash, View } from '@/primitives/Icons';
import { Input } from '@/primitives/Input';
import { dateFormatter } from '@/utils/format';
import { useDebounce } from '@/utils/hooks';
import { agentApi } from '@/windows/queries/agent/api';
import { useCancelOrder } from '@/windows/queries/agent/queries';

import { useWorkspaceFilterStore } from '../api/workspaceFilterStore';
import { StatusBadge } from '../blocks/StatusBadge';
import { keys } from '../queries/agent/keys';
import styles from './AgentWorkspace.module.css';
import { redirectToLoginPage } from './Errors';

interface AgentWorkspaceContextType {
  data: PaginatedData<WindowOrderSummary>;
  setData: (data: PaginatedData<WindowOrderSummary>) => void;
  replaceOrder: (order: WindowOrderSummary) => void;
}

const AgentWorkspaceContext = createContext<AgentWorkspaceContextType | null>(
  null
);

export const AgentOrdersProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [data, setOrders] = useState<PaginatedData<WindowOrderSummary>>({
    rows: [],
    totalPages: 0,
    totalRows: 0,
  });

  const setData = React.useCallback(
    (data: PaginatedData<WindowOrderSummary>) => {
      setOrders(data);
    },
    [setOrders]
  );

  const replaceOrder = React.useCallback(
    (order: WindowOrderSummary) => {
      setOrders((prev) => {
        return {
          ...prev,
          rows: prev.rows?.map((row) => (row.id === order.id ? order : row)),
        };
      });
    },
    [setOrders]
  );

  return (
    <AgentWorkspaceContext.Provider
      value={{
        data,
        setData,
        replaceOrder,
      }}
    >
      {children}
    </AgentWorkspaceContext.Provider>
  );
};

export const useAgentOrders = () => {
  const context = useContext(AgentWorkspaceContext);
  if (!context) {
    throw new Error(
      'useAgentOrders must be used within an AgentOrdersProvider'
    );
  }
  return context;
};

type Column<T> = {
  accessorKey: string;
  cell?: ({ row }: { row: T }) => React.ReactNode;
  style?: React.CSSProperties;
};

export const AgentWorkspace = () => {
  return (
    <AgentOrdersProvider>
      <AgentOrders />
    </AgentOrdersProvider>
  );
};

const AgentOrders = () => {
  const { search, statuses, dateFilters, pagination, setPagination } =
    useWorkspaceFilterStore();
  const debouncedSearch = useDebounce(search, 700, search);
  const debouncedStatuses = useDebounce(statuses, 500, statuses);

  const lastSearch = React.useRef(debouncedSearch);
  const { setData } = useAgentOrders();

  const queryClient = useQueryClient();

  // @ts-ignore
  const columns: Column<WindowOrderSummary>[] = [
    {
      accessorKey: 'title',
    },
    {
      accessorKey: 'shortId',
    },
    {
      accessorKey: 'resellerCompanyName',
      cell: ({ row }: { row: WindowOrderSummary }) =>
        row.userLead?.companyName || '-',
    },
    {
      accessorKey: 'resellerName',
      cell: ({ row }: { row: WindowOrderSummary }) => row.userLead?.name || '-',
    },
    {
      accessorKey: 'resellerEmail',
      cell: ({ row }: { row: WindowOrderSummary }) =>
        row.userLead?.email || '-',
    },
    {
      accessorKey: 'createdAt',
      cell: ({ row }: { row: WindowOrderSummary }) =>
        dateFormatter(row.createdAt),
    },
    {
      accessorKey: 'status',
      cell: ({ row }: { row: WindowOrderSummary }) => (
        <StatusBadge order={row} />
      ),
      style: {
        textAlign: 'center',
        maxWidth: '64px',
      },
    },
    {
      accessorKey: 'actions',
      cell: ({ row }: { row: WindowOrderSummary }) => (
        <div className={styles.actions}>
          <CancelOrderButton row={row} />
        </div>
      ),
      style: {
        textAlign: 'center',
      },
    },
  ];

  React.useEffect(() => {
    const { createdAfter, createdBefore, submittedAfter, submittedBefore } =
      dateFilters;
    const queryParams = {
      ...pagination,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(debouncedStatuses && debouncedStatuses.length > 0
        ? { statuses: debouncedStatuses }
        : {}),
      ...(createdAfter ? { createdAfter } : {}),
      ...(createdBefore ? { createdBefore } : {}),
      ...(submittedAfter ? { submittedAfter } : {}),
      ...(submittedBefore ? { submittedBefore } : {}),
    };

    queryClient
      .fetchQuery<PaginatedData<WindowOrderSummary>>({
        queryKey: keys.getOrders(queryParams),
        queryFn: () => agentApi.getAllOrders(queryParams),
      })
      .then((data) => {
        setData(data);
        if (lastSearch.current !== debouncedSearch) {
          setPagination({
            pageSize: pagination.pageSize,
            pageIndex: 0,
          });
          lastSearch.current = debouncedSearch;
        }
      })
      .catch((error) => {
        if (error instanceof ApiError && error.response?.status === 401) {
          return redirectToLoginPage('/admin/');
        }
        throw error;
      });
  }, [
    pagination,
    debouncedSearch,
    debouncedStatuses,
    dateFilters,
    queryClient,
    setPagination,
    setData,
  ]);

  return (
    <main className={styles.content}>
      <Filters />
      <div className={styles.orderSection}>
        <AgentOrdersTable columns={columns} />
      </div>
    </main>
  );
};

type AgentOrdersTableProps = {
  columns: Column<WindowOrderSummary>[];
};

const AgentOrdersTable = ({ columns }: AgentOrdersTableProps) => {
  const { t } = useTranslation();
  const isFetching = useIsFetching({
    queryKey: ['windows', 'agent', 'orders'],
  });

  const { pagination, setPagination } = useWorkspaceFilterStore();

  const { data } = useAgentOrders();

  const rows = data.rows;
  const pageMetadata = {
    ...pagination,
    totalRows: data.totalRows ?? 0,
    totalPages: data.totalPages ?? 0,
  };

  return (
    <>
      <table>
        <thead>
          <tr>
            {columns.map(({ accessorKey, style = {} }) => (
              <th key={accessorKey} style={style}>
                {t(`agent.${accessorKey}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <Rows rows={rows} columns={columns} />
        </tbody>
      </table>
      <div className="flexAlign opposites">
        <div style={{ width: '100%' }}>
          <BasePagination
            totalPages={pageMetadata.totalPages}
            currentPage={pageMetadata.pageIndex + 1}
            disabled={isFetching > 0}
            onPageChanged={(page) => {
              setPagination({
                pageSize: pageMetadata.pageSize,
                pageIndex: page - 1,
              });
            }}
          />
        </div>
        <div className="flexAlign">
          <span style={{ whiteSpace: 'nowrap' }}>{t('agent.show')}</span>
          {[10, 30, 50].map((pageSize) => (
            <Button
              key={pageSize}
              size="xs"
              className={pageMetadata.pageSize !== pageSize ? 'outline' : ''}
              disabled={isFetching > 0}
              onClick={() =>
                pageMetadata.pageSize !== pageSize &&
                setPagination({
                  pageSize,
                  pageIndex: 0,
                })
              }
            >
              {pageSize}
            </Button>
          ))}
        </div>
      </div>
    </>
  );
};

const Rows = ({
  rows,
  columns,
}: {
  rows?: WindowOrderSummary[];
  columns: Column<WindowOrderSummary>[];
}) => {
  const isFetching = useIsFetching({
    queryKey: ['windows', 'agent', 'orders'],
  });

  if (rows && rows.length > 0) {
    return (
      <>
        {rows.map((row) => (
          <TableRow key={row.id} row={row} columns={columns} />
        ))}
      </>
    );
  }

  if (rows && rows.length === 0) {
    return <NoResultsRow columns={columns} />;
  }

  if (isFetching) {
    return (
      <>
        {Array.from({ length: 10 }).map((_, index) => (
          <SkeletonRow key={index} columns={columns} />
        ))}
      </>
    );
  }

  return <NoResultsRow columns={columns} />;
};

const CancelOrderButton = ({ row }: { row: WindowOrderSummary }) => {
  const { t } = useTranslation();
  const { replaceOrder } = useAgentOrders();
  const { mutateAsync: cancelOrder, isPending } = useCancelOrder(row.id);
  const [isOpen, setIsOpen] = React.useState(false);

  const isCancellable =
    row.status !== WINDOW_ORDER_STATUS.cancelled &&
    row.status !== WINDOW_ORDER_STATUS.order_placed;

  if (!isCancellable) return null;

  const handleConfirm = async () => {
    setIsOpen(false);
    try {
      await cancelOrder();
      replaceOrder({ ...row, status: WINDOW_ORDER_STATUS.cancelled });
      toast.success(t('agent.quoteSummary.cancelOrderSuccess'));
    } catch (error) {
      toast.error(
        t('agent.quoteSummary.cancelOrderError', {
          error: (error as Error)?.message,
        })
      );
    }
  };

  return (
    <>
      <Button
        variant="icon"
        className="delete"
        icon={<Icon icon={<Trash />} />}
        disabled={isPending}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      ></Button>
      <div
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        <ConfirmationDialog
          isOpen={isOpen}
          title={t('agent.quoteSummary.cancelOrder')}
          onConfirm={handleConfirm}
          onClose={() => setIsOpen(false)}
          variant="alert"
        >
          {t('agent.quoteSummary.cancelOrderConfirm')}
        </ConfirmationDialog>
      </div>
    </>
  );
};

const TableRow = ({
  row,
  columns,
}: {
  row: WindowOrderSummary;
  columns: Column<WindowOrderSummary>[];
}) => {
  const navigate = useNavigate();
  const { id } = row;

  return (
    <tr
      key={row.id}
      onClick={() => navigate(`/admin/windows/orders/${id}`)}
      className={styles.clickableRow}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          navigate(`/admin/windows/orders/${id}`);
        }
      }}
      aria-label={`Go to order ${id}`}
    >
      {columns.map(({ accessorKey, cell, style = {} }) => (
        <td key={`${accessorKey} + ${row.id}`} style={style}>
          {/* @ts-ignore good luck */}
          {cell ? cell({ row }) : row[accessorKey]}
        </td>
      ))}
    </tr>
  );
};

const SkeletonRow = ({
  columns,
}: {
  columns: Column<WindowOrderSummary>[];
}) => {
  return (
    <tr style={{ opacity: 0.7 }}>
      {columns.map((_, index) => (
        <td key={index} className={styles.loading}>
          <div className={styles.bar}></div>
        </td>
      ))}
      <td>
        <div className={styles.actions}>
          <Button className="smaller pill snug outline" disabled={true}>
            <Icon icon={<Download />} />
          </Button>
          <Button className="smaller pill snug outline" disabled={true}>
            <Icon icon={<View />} />
          </Button>
          <Button className="smaller pill snug outline" disabled={true}>
            <Icon icon={<Edit />} />
          </Button>
        </div>
      </td>
    </tr>
  );
};

const NoResultsRow = ({
  columns,
}: {
  columns: Column<WindowOrderSummary>[];
}) => {
  const { t } = useTranslation();
  return (
    <tr>
      <td
        colSpan={columns.length + 1}
        style={{ textAlign: 'center', padding: '20px' }}
      >
        {t('agent.noResultsFound')}
      </td>
    </tr>
  );
};

const Filters = () => {
  const { t } = useTranslation();
  const {
    dateFilters,
    setDateFilters,
    search,
    setSearch,
    statuses,
    setStatuses,
  } = useWorkspaceFilterStore();
  const { createdAfter, createdBefore, submittedAfter, submittedBefore } =
    dateFilters;

  // backend does not support date filters yet
  const dateFiltersActive = false;

  return (
    <div className={styles.filterSection}>
      <div className={styles.filterField}>
        <label>{t('agent.filters.search')}</label>
        <Input
          type="text"
          placeholder={t('agent.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className={styles.filterField}>
        <label>{t('agent.filters.status')}</label>
        <Multiselect
          id="status"
          value={t('agent.statuses')}
          options={WINDOW_ORDER_STATUS_OPTIONS.map((value) => ({
            label: t(`agent.orderStatus.${value}`),
            value,
            selected: statuses.includes(value),
            component: () => <StatusBadge status={value} />,
          }))}
          onSelected={(selectedValues) => {
            setStatuses([...selectedValues]);
          }}
          className={styles.filterSelect}
        />
      </div>

      {dateFiltersActive && (
        <>
          <div className={styles.filterField}>
            <label>{t('agent.filters.createdAfter')}</label>
            <Input
              type="date"
              value={createdAfter}
              onChange={(e) =>
                setDateFilters({ ...dateFilters, createdAfter: e.target.value })
              }
            />
          </div>

          <div className={styles.filterField}>
            <label>{t('agent.filters.createdBefore')}</label>
            <Input
              type="date"
              value={createdBefore}
              onChange={(e) =>
                setDateFilters({
                  ...dateFilters,
                  createdBefore: e.target.value,
                })
              }
            />
          </div>

          <div className={styles.filterField}>
            <label>{t('agent.filters.submittedAfter')}</label>
            <Input
              type="date"
              value={submittedAfter}
              onChange={(e) =>
                setDateFilters({
                  ...dateFilters,
                  submittedAfter: e.target.value,
                })
              }
            />
          </div>

          <div className={styles.filterField}>
            <label>{t('agent.filters.submittedBefore')}</label>
            <Input
              type="date"
              value={submittedBefore}
              onChange={(e) =>
                setDateFilters({
                  ...dateFilters,
                  submittedBefore: e.target.value,
                })
              }
            />
          </div>
        </>
      )}
    </div>
  );
};
